import { trackGoogleAdsConversion } from 'analytics';
import forge from 'apis/forge';
import HomeBar from 'components/Home/HomeBar';
import { AuthUser } from 'dragonmouse-shared/interfaces';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setAuthUser } from 'redux/mode/actions';
import { hasEarlyAccess, mixpanelIdentify, mixpanelTrackEvent } from 'utils';
import * as S from './styles';

interface InviteCodeProps {
	email: string;
}

const InviteCode = ({ email }: InviteCodeProps) => {
	const dispatch = useDispatch();

	const [inviteCode, setInviteCode] = useState('');
	const [isRedeeming, setIsRedeeming] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// extract invite code from URL query parameter on component mount
	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const codeFromUrl = urlParams.get('code');
		if (codeFromUrl) {
			setInviteCode(codeFromUrl);
		}
	}, []);

	const handleRedeemCode = async () => {
		const code = inviteCode.trim();
		if (!code) {
			return;
		}

		setIsRedeeming(true);
		setError(null);

		try {
			await forge.redeemInviteCode({ code });

			// Fetch updated auth user with new permissions
			// Auth0 may take a moment to propagate role changes, so retry a few times
			let updatedAuthUser = await forge.getAuthUser();
			const maxRetries = 5;
			let retries = 0;

			while (!hasEarlyAccess(updatedAuthUser) && retries < maxRetries) {
				// Wait a bit before retrying (exponential backoff: 200ms, 400ms, 800ms, 1600ms, 3200ms)
				await new Promise((resolve) => setTimeout(resolve, 200 * Math.pow(2, retries)));
				updatedAuthUser = await forge.getAuthUser();
				retries++;
			}

			mixpanelTrackEvent('Invite Code Redeemed', { code });
			mixpanelIdentify({ $invite_codes: [code] });
			if (hasEarlyAccess(updatedAuthUser)) {
				// go ahead and initialize the user (so we don't have to depend on the App initializing the user -- app loading sequence is nuanced)
				const { userCreated } = await forge.initializeUser();
				if (userCreated) {
					console.log('*- invite code -*');
					trackGoogleAdsConversion();
				}
				dispatch(setAuthUser(updatedAuthUser as AuthUser));
				window.location.reload();
				return; // Exit early since we're reloading
			}

			// Edge case: redemption succeeded but user doesn't have early_access
			// Don't reload - would just show invite code page again
			console.error('Invite code redeemed, but access was not granted.');
			setError('Invite code redeemed, but access was not granted. Try reloading or contact support.');
			dispatch(setAuthUser(updatedAuthUser as AuthUser));
		} catch (err: any) {
			setError(err.response?.data?.error || 'Failed to redeem invite code');
		} finally {
			setIsRedeeming(false);
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	return (
		<S.Base>
			<HomeBar />
			<S.Content>
				<S.LeftColumn>
					<S.Headline>You&rsquo;re on the list</S.Headline>
					<S.Body>
						We&rsquo;ve added <S.EmailAddress>{email}</S.EmailAddress> to our waitlist. We&rsquo;ll
						notify you as soon as spots open up.
					</S.Body>
					<S.Body>Got a code? Enter it below for instant access.</S.Body>
					<S.InviteCodeContainer>
						<S.InviteCodeInput
							value={inviteCode}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInviteCode(e.target.value)}
							onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
								if (e.key === 'Enter' && !isRedeeming && inviteCode.trim()) {
									handleRedeemCode();
								}
							}}
							placeholder="Enter invite code"
							disabled={isRedeeming}
						/>
						<S.Button onClick={handleRedeemCode} disabled={isRedeeming || !inviteCode.trim()}>
							{isRedeeming ? 'Submitting...' : 'Submit'}
						</S.Button>
					</S.InviteCodeContainer>
					<S.ErrorMessage>{error || ' '}</S.ErrorMessage>
					<S.Body>
						Really need access now? Email{' '}
						<S.EmailLink href="mailto:hello@moonchild.ai">hello@moonchild.ai</S.EmailLink> and tell
						us why. We read every message.
					</S.Body>
				</S.LeftColumn>
				<S.RightColumn>
					<img
						src="/images/skating-cat.png"
						alt="An illustrated cat riding a skateboard with a moon in the background"
					/>
				</S.RightColumn>
			</S.Content>
		</S.Base>
	);
};

export default InviteCode;
